(function () {
  "use strict";

  function throttleRAF(fn) {
    let raf = null;
    let lastArgs = null;

    const wrapped = function (...args) {
      lastArgs = args;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        fn.apply(this, lastArgs);
      });
    };

    wrapped.cancel = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = null;
    };

    return wrapped;
  }

  function clamp01(v) {
    return Math.max(0, Math.min(1, v));
  }

  function parseNumberAttr(el, name, fallback) {
    const v = parseFloat(el.getAttribute(name));
    return Number.isFinite(v) ? v : fallback;
  }

  class SplitLines extends HTMLElement {
    constructor() {
      super();

      this._requireSplit = true;
      this._lastScreenWidth = window.innerWidth;

      this.attachShadow({ mode: "open" });
      this.shadowRoot.appendChild(document.createRange().createContextualFragment("<slot></slot>"));

      this._onResize = throttleRAF(this._onWindowResized.bind(this));
      this._onMutation = () => {
        this._requireSplit = true;
        this._split();
      };

      this._mutationObserver = new MutationObserver(this._onMutation);
    }

    connectedCallback() {
      window.addEventListener("resize", this._onResize);

      this._mutationObserver.observe(this, {
        characterData: true,
        childList: true,
        subtree: true,
      });

      if (this.hasAttribute("split-on-insert")) this._split();
    }

    disconnectedCallback() {
      window.removeEventListener("resize", this._onResize);
      if (this._onResize && this._onResize.cancel) this._onResize.cancel();
      if (this._mutationObserver) this._mutationObserver.disconnect();
    }

    get lines() {
      this._split();
      return Array.from(this.shadowRoot.children);
    }

    get preserveLetters() {
      return this.hasAttribute("preserve-letters");
    }

    _split(force = false) {
      if (!this._requireSplit && !force) return;

      const text = (this.textContent || "").trimEnd();

      if (!text) {
        this.shadowRoot.innerHTML = "";
        this._requireSplit = false;
        this.classList.add("is-split");
        this.dispatchEvent(new CustomEvent("split-lines:split", { bubbles: true, detail: { lines: [] } }));
        return;
      }

      // Wrap each character
      this.shadowRoot.innerHTML = text
        .replace(/./g, '<span part="letter">$&</span>')
        .replace(/\s/g, " ");

      // Group letters by line using offsetTop
      const bounds = new Map();
      Array.from(this.shadowRoot.children).forEach((letter) => {
        const key = Math.round(letter.offsetTop);
        const current = bounds.get(key) || "";
        bounds.set(key, current + (this.preserveLetters ? letter.outerHTML : letter.textContent));
      });

      // Rebuild as lines, inserting a space text node between each line span so
      // that words don't concatenate when two line spans happen to share a visual row.
      const lineValues = Array.from(bounds.values());
      const nodes = [];
      lineValues.forEach((line, i) => {
        nodes.push(
          document.createRange().createContextualFragment(
            '<span part="line" style="display: inline-block;">' + line + "</span>"
          )
        );
        if (i < lineValues.length - 1) nodes.push(document.createTextNode(" "));
      });

      this.shadowRoot.replaceChildren(...nodes);
      this._requireSplit = false;

      this.classList.add("is-split");
      this.dispatchEvent(
        new CustomEvent("split-lines:split", {
          bubbles: true,
          detail: { lines: Array.from(this.shadowRoot.children) },
        })
      );
    }

    _onWindowResized() {
      if (this._lastScreenWidth !== window.innerWidth) {
        this._split(true);
        this._lastScreenWidth = window.innerWidth;
      }
    }
  }

  const readingInstances = new Set();
  let globalScrollBound = false;
  const requestGlobalUpdate = throttleRAF(() => {
    readingInstances.forEach((inst) => inst._updateScrollProgress());
  });

  function bindGlobalScroll() {
    if (globalScrollBound) return;
    globalScrollBound = true;

    window.addEventListener("scroll", requestGlobalUpdate, { passive: true });
    window.addEventListener("resize", requestGlobalUpdate);
  }

  function unbindGlobalScrollIfNeeded() {
    if (!globalScrollBound) return;
    if (readingInstances.size) return;

    window.removeEventListener("scroll", requestGlobalUpdate, { passive: true });
    window.removeEventListener("resize", requestGlobalUpdate);
    requestGlobalUpdate.cancel();
    globalScrollBound = false;
  }

  class ReadingText extends HTMLElement {
    constructor() {
      super();

      this._resizeObserver = null;
      this._items = [];
      this._calculating = false;

      this._onSplitLines = this._calculate.bind(this);
    }

    connectedCallback() {
      // Keep subheading visible if your CSS hides [reveal-on-scroll]
      const subheading = this.querySelector(".section-subheading[reveal-on-scroll], .subheading[reveal-on-scroll]");
      if (subheading) subheading.removeAttribute("reveal-on-scroll");

      this.addEventListener("split-lines:split", this._onSplitLines);

      if (!this._resizeObserver) {
        this._resizeObserver = new ResizeObserver(this._calculate.bind(this));
        this._resizeObserver.observe(this);
      }

      readingInstances.add(this);
      bindGlobalScroll();

      this._calculate();
      this._updateScrollProgress();
    }

    disconnectedCallback() {
      this.removeEventListener("split-lines:split", this._onSplitLines);

      if (this._resizeObserver) this._resizeObserver.disconnect();
      this._resizeObserver = null;

      readingInstances.delete(this);
      unbindGlobalScrollIfNeeded();

      this._items = [];
      this._calculating = false;
    }

    get readingSpeed() {
      return parseNumberAttr(this, "reading-speed", 0.9);
    }

    get textStartOpacity() {
      return parseNumberAttr(this, "text-start-opacity", 0.2);
    }

    _getFillSplits() {
      return Array.from(this.querySelectorAll("split-lines[fill-on-scroll]"));
    }

    _collectItems() {
      const items = [];

      this._getFillSplits().forEach((split) => {
        const lines = split.lines || [];
        if (!lines.length) return;

        const splitStartOpacity = parseNumberAttr(split, "fill-start-opacity", this.textStartOpacity);

        lines.forEach((line) => {
          Array.from(line.children).forEach((ch) => {
            ch.dataset.fillStartOpacity = String(splitStartOpacity);
            items.push(ch);
          });
        });
      });

      return items;
    }

    _calculate() {
      if (this._calculating) return;
      this._calculating = true;

      this._items = this._collectItems();

      const length = this._items.length;
      this.style.setProperty("--reading-text-scroll-height", String(this.readingSpeed * length) + "vh");

      // Set initial opacity for letters
      this._items.forEach((item) => {
        const startOpacity = parseFloat(item.dataset.fillStartOpacity || "");
        const so = Number.isFinite(startOpacity) ? startOpacity : this.textStartOpacity;
        item.style.opacity = String(so);
      });

      this._calculating = false;
      this._updateScrollProgress();
    }

    _getScrollProgress() {
      const rect = this.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight || 0;

      const denom = rect.height + vh;
      if (!denom) return 0;

      let rawProgress = (vh - rect.top) / denom;
      
      const startDelay = 0.1;
      const endEarly = 0.1;
      const progressRange = 1 - startDelay - endEarly;
      let p = (rawProgress - startDelay) / progressRange;
      
      if (this.classList.contains('sticky-text')) {
        p = p / 0.8;
      } else if (this.classList.contains('block-reading-text')) {
        p = p / 0.85;
      }
      
      return clamp01(p);
    }

    _updateScrollProgress() {
      const items = this._items;
      if (!items.length) return;

      const progress = this._getScrollProgress();
      const step = 100 / (items.length + 15);

      for (let index = 0; index < items.length; index++) {
        const item = items[index];

        const start = (index * step) / 100;
        const end = ((index + 16) * step) / 100;

        const t = clamp01((progress - start) / (end - start));

        const startOpacity = parseFloat(item.dataset.fillStartOpacity || "");
        const so = Number.isFinite(startOpacity) ? startOpacity : 0.2;

        item.style.opacity = String(so + (1 - so) * t);
      }
    }
  }

  if (!window.customElements.get("split-lines")) window.customElements.define("split-lines", SplitLines);
  if (!window.customElements.get("reading-text")) window.customElements.define("reading-text", ReadingText);
})();
